import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { getErrorMessage, imageTypes, plataformasActivas } from "../../../../common";
import CoursesService from "../../../../services/campus/courses.service";
import PlanDePagos from "../../../../components/planDePagos/planDePagos";
import { PreciosPlataforma } from "../../../../components/plataformas/plataformas";
import PanelDeNivel from "../../portalClase/contenido/panelesDeNivel";
import { CourseBlocksPanel } from "../../../management/courseBlocksPrompt";
import { Chip, claveTab, Dato, nombreMaestra, Seccion, Ventana } from "./piezas";
import { PermisosModulo } from "./moduloVentana";
import useEsTelefono from "../../../../utils/esTelefono";
import { CORTE_MOVIL } from "./piezas";

/*
	Ventana del curso (Horarios Claros, 2026-11-01): los datos del curso con su botón Editar y, abajo, pestañas con sus Módulos, el Plan
	de Pagos (Precios), los Bloques (cursos compuestos) y el Contenido y la Evaluación que heredan los horarios. Guarda con el mismo
	CoursesService.saveCourse de siempre.
*/

interface Props {
	campusId: number;
	campus: any;
	/** null = curso nuevo. */
	curso: any | null;
	modos: any[];
	categorias: any[];
	vivos: { [levelId: number]: number } | null;
	permisos: PermisosModulo;
	permisosModulos: PermisosModulo;
	puedeContenido: boolean;
	onClose: () => void;
	onAbrirModulo: (nivel: any | null) => void;
	/** Recarga la lista; con cerrar = true la ventana se cierra. */
	onChanged: (texto: string, cerrar: boolean) => Promise<void>;
}

type Pestana = "modulos" | "precios" | "bloques" | "contenido" | "evaluacion";

const borradorDe = (c: any | null, modos: any[], categorias: any[]) => ({
	name: c?.name || "", code: c?.code || "", status_id: c ? Number(c.status_id) : 1,
	mode_id: c ? Number(c.mode_id) : (modos[0]?.id ?? 1), category_id: c ? Number(c.category_id) : (categorias[0]?.id ?? 1),
	description: c?.description || "", image: c?.image ?? null, imagePreview: c?.image ?? null
});

const kitsTexto = (n: any): React.ReactNode => {
	const ks: any[] = n.kits || [];
	if (!ks.length) return <span className="wcuOro">Sin Kit</span>;
	return <>{ks[0].name}{ks.length > 1 ? <span className="wcuSub"> +{ks.length - 1}</span> : null}</>;
};

const CursoVentana: React.FC<Props> = ({ campusId, campus, curso, modos, categorias, vivos, permisos, permisosModulos, puedeContenido, onClose, onAbrirModulo, onChanged }) => {
	const movil = useEsTelefono(CORTE_MOVIL);
	const esNuevo = curso === null;
	const [pestana, setPestana] = useState<Pestana>("modulos");
	const [editando, setEditando] = useState<boolean>(esNuevo);
	const [borrador, setBorrador] = useState(borradorDe(curso, modos, categorias));
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmando, setConfirmando] = useState<boolean>(false);
	const archivo = useRef<HTMLInputElement>(null);

	const poner = (k: string, v: any) => setBorrador((b) => ({ ...b, [k]: v }));
	const niveles: any[] = (curso?.levels || []).slice().sort((a: any, b: any) => String(a.name).localeCompare(String(b.name), "es", { numeric: true }));

	const elegirFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files && e.target.files[0];
		if (!f) return;
		if (f.size > 5242880 || imageTypes.indexOf(f.type) < 0) { setError("Solo se permiten imágenes de hasta 5 MB en formato BMP, GIF, JPEG, PNG o WEBP."); e.target.value = ""; return; }
		const lector = new FileReader();
		lector.onloadend = () => { setBorrador((b) => ({ ...b, image: f, imagePreview: String(lector.result) })); setError(null); };
		lector.readAsDataURL(f);
		e.target.value = "";
	};

	const cancelar = () => {
		if (esNuevo) { onClose(); return; }
		setBorrador(borradorDe(curso, modos, categorias)); setEditando(false); setError(null);
	};

	const guardar = async () => {
		if (borrador.name.trim() === "") { setError("Falta el nombre del curso."); return; }
		if (!esNuevo && borrador.code.trim() === "") { setError("Falta el código del curso."); return; }
		setBusy(true); setError(null);
		try {
			const r: any = await CoursesService.saveCourse({ id: campusId },
				{ ...(curso || {}), ...borrador, name: borrador.name.trim(), code: esNuevo ? "" : borrador.code.trim(), tags: curso?.tags || [], id: curso?.id ?? null });
			await onChanged(esNuevo ? "Curso creado" : "Curso guardado", esNuevo);
			if (!esNuevo) setEditando(false);
			void r;
		} catch (e: any) {
			setError(e?.response?.status === 409 ? "Ya existe un curso con ese nombre o código en este plantel." : getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const borrar = async () => {
		setBusy(true); setError(null);
		try {
			await CoursesService.removeCourses(campusId, [curso.id]);
			await onChanged("Curso borrado", true);
		} catch (e: any) {
			setError(getErrorMessage(e)); setConfirmando(false);
		} finally {
			setBusy(false);
		}
	};

	const tabs: { k: Pestana; t: string; n?: number }[] = [
		{ k: "modulos", t: "Módulos", n: niveles.length },
		{ k: "precios", t: "Precios" },
		{ k: "bloques", t: "Bloques" },
		...(puedeContenido ? [{ k: "contenido" as Pestana, t: "Contenido" }, { k: "evaluacion" as Pestana, t: "Evaluación" }] : [])
	];

	const modo = modos.find((m) => Number(m.id) === Number(curso?.mode_id));
	const categoria = categorias.find((c) => Number(c.id) === Number(curso?.category_id));

	const formulario = (
		<div className="wcuForm">
			<div className="ancho" style={{ display: "flex", alignItems: "center", gap: 12 }}>
				{borrador.imagePreview ? <img className="wcuFoto" alt="" src={borrador.imagePreview} /> : <div className="wcuFotoVacia">Sin imagen</div>}
				<input ref={archivo} type="file" accept="image/*" style={{ display: "none" }} onChange={elegirFoto} />
				<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => archivo.current?.click()}><FontAwesomeIcon icon={solid("camera")} />&nbsp;Cambiar Foto</button>
			</div>
			<label className="wau-campo"><span className="wau-lab">Nombre</span>
				<input className="wau-entrada" type="text" maxLength={50} value={borrador.name} disabled={busy} onChange={(e) => poner("name", e.target.value)} /></label>
			<label className="wau-campo"><span className="wau-lab">Código</span>
				<input className="wau-entrada" type="text" maxLength={50} value={esNuevo ? "" : borrador.code} placeholder={esNuevo ? "Se genera al guardar" : ""} disabled={busy || esNuevo} onChange={(e) => poner("code", e.target.value)} /></label>
			<label className="wau-campo"><span className="wau-lab">Estado</span>
				<select className="wau-entrada" value={borrador.status_id} disabled={busy} onChange={(e) => poner("status_id", Number(e.target.value))}>
					<option value={1}>Activo</option><option value={0}>Inactivo</option></select></label>
			<label className="wau-campo"><span className="wau-lab">Modalidad</span>
				<select className="wau-entrada" value={borrador.mode_id} disabled={busy} onChange={(e) => poner("mode_id", Number(e.target.value))}>
					{modos.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
			<label className="wau-campo"><span className="wau-lab">Categoría</span>
				<select className="wau-entrada" value={borrador.category_id} disabled={busy} onChange={(e) => poner("category_id", Number(e.target.value))}>
					{categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
			<label className="wau-campo ancho"><span className="wau-lab">Descripción</span>
				<textarea className="wau-entrada" maxLength={255} value={borrador.description} disabled={busy} onChange={(e) => poner("description", e.target.value)} /></label>
			<div className="ancho" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
				<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={cancelar}>Cancelar</button>
				<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={guardar}>{busy ? "Guardando" : esNuevo ? "Crear Curso" : "Guardar"}</button>
			</div>
		</div>
	);

	const lectura = curso ? (
		<div className="wcuDatos">
			{curso.image ? <div className="wcuDato"><img className="wcuFoto" alt="" src={curso.image} /></div> : null}
			<Dato t="Código">{curso.code || "-"}</Dato>
			<Dato t="Modalidad">{modo?.name || curso.mode?.name || "-"}</Dato>
			<Dato t="Categoría">{categoria?.name || curso.category?.name || "-"}</Dato>
			{curso.description ? <Dato t="Descripción" ancho>{curso.description}</Dato> : null}
		</div>
	) : null;

	const tabModulos = (
		<>
			<div className="wcuSecCab" style={{ justifyContent: "flex-end" }}>
				{permisosModulos.create ? <button type="button" className="btn btn-primary btn-sm" onClick={() => onAbrirModulo(null)}><FontAwesomeIcon icon={solid("plus")} />&nbsp;Nuevo Módulo</button> : null}
			</div>
			{!niveles.length ? <div className="wcuVacio">Este curso todavía no tiene módulos.</div> : movil ? (
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{niveles.map((n) => (
						<div key={n.id} className="wcuTarjeta" role="button" tabIndex={0} onClick={() => onAbrirModulo(n)} onKeyDown={(e) => { if (e.key === "Enter") onAbrirModulo(n); }}>
							<div className="fila"><div><b>{n.name}</b><div className="wcuSub">{n.code}</div></div>{Number(n.status_id) !== 1 ? <Chip>Inactivo</Chip> : null}</div>
							<div style={{ fontSize: 14, marginTop: 4 }}>{kitsTexto(n)}</div>
							<div className="wcuSub" style={{ marginTop: 2 }}>Maestras {(n.mentors || []).length}, Horarios Vivos {vivos ? (vivos[n.id] ?? 0) : "…"}</div>
						</div>
					))}
				</div>
			) : (
				<div className="waScrollX" style={{ overflowX: "auto" }}>
					<table className="table table-hover wcuTabla">
						<thead><tr><th>Módulo</th><th>Kit</th><th className="wcuNum">Maestras</th><th className="wcuNum">Horarios Vivos</th><th>Estado</th></tr></thead>
						<tbody>
							{niveles.map((n) => (
								<tr key={n.id} className="clic" onClick={() => onAbrirModulo(n)}>
									<td><b>{n.name}</b><div className="wcuSub">{n.code}</div></td>
									<td>{kitsTexto(n)}</td>
									<td className="wcuNum" title={(n.mentors || []).map(nombreMaestra).join(", ")}>{(n.mentors || []).length}</td>
									<td className="wcuNum">{vivos ? (vivos[n.id] ?? 0) : "…"}</td>
									<td><Chip tono={Number(n.status_id) === 1 ? "acento" : ""}>{Number(n.status_id) === 1 ? "Activo" : "Inactivo"}</Chip></td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);

	const pie = !esNuevo && permisos.delete ? (
		confirmando ? <>
			<span style={{ fontSize: 14 }}>¿Borrar este curso?</span>
			<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setConfirmando(false)}>Cancelar</button>
			<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={borrar}>Sí, Borrar</button>
		</> : <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setConfirmando(true)}>Borrar Curso</button>
	) : undefined;

	return (
		<Ventana xl onClose={onClose} busy={busy} pie={pie} titulo={<>
			<span>{esNuevo ? "Nuevo Curso" : curso.name}</span>
			{!esNuevo ? <Chip tono={Number(curso.status_id) === 1 ? "acento" : ""}>{Number(curso.status_id) === 1 ? "Activo" : "Inactivo"}</Chip> : null}
			{curso?.isComposite ? <Chip>Compuesto</Chip> : null}
		</>}>
			{error ? <div className="alert alert-danger" style={{ fontSize: 14.5 }}>{error}</div> : null}
			{editando ? formulario : <>
				{lectura}
				{permisos.update ? <div style={{ marginTop: 10 }}><button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setBorrador(borradorDe(curso, modos, categorias)); setEditando(true); }}><FontAwesomeIcon icon={solid("pen")} />&nbsp;Editar</button></div> : null}
			</>}
			{!esNuevo ? <>
				<ul className="nav nav-pills waTabs wcuTabs" style={{ gap: 4 }}>
					{tabs.map((x) => (
						<li className="nav-item" key={claveTab(x.k)}>
							<button type="button" className={"nav-link btn btn-sm " + (pestana === x.k ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => setPestana(x.k)}>
								{x.t}{x.n ? <span className="waTabCuenta">{x.n}</span> : null}
							</button>
						</li>
					))}
				</ul>
				{pestana === "modulos" ? tabModulos : null}
				{pestana === "precios" ? <>
					<PlanDePagos campusId={campusId} courseId={Number(curso.id)} puedeEditar={!!permisos.update} />
					{plataformasActivas(campus) ? <PreciosPlataforma campusId={campusId} courseId={Number(curso.id)} puedeEditar={!!permisos.update} /> : null}
				</> : null}
				{pestana === "bloques" ? <Seccion><CourseBlocksPanel campusId={campusId} course={{ id: curso.id, name: curso.name, code: curso.code }} allowedUpdate={!!permisos.update} /></Seccion> : null}
				{pestana === "contenido" ? <PanelDeNivel key="contenido" pestana="contenido" nivel="curso" id={curso.id} /> : null}
				{pestana === "evaluacion" ? <PanelDeNivel key="evaluacion" pestana="evaluacion" nivel="curso" id={curso.id} /> : null}
			</> : null}
		</Ventana>
	);
};

export default CursoVentana;
